import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  Tooltip,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Pagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  useTheme,
  useMediaQuery,
  Typography as MuiTypography,
} from '@mui/material'
import { Visibility, Refresh } from '@mui/icons-material'
import { useEmailManagement } from '../../../hooks/useEmailManagement'
import { getEmailTypeLabel } from '../../../utils/emailTypeOptions'

const STATUS_COLORS = {
  PENDING: 'warning',
  SENT: 'success',
  FAILED: 'error',
  PARTIAL: 'info',
  CANCELLED: 'default',
}

const parseBackendUtcDate = (dateString) => {
  if (!dateString) return null

  // If timezone already exists in payload, trust native parsing.
  if (
    dateString.endsWith('Z') ||
    /[+-]\d{2}:\d{2}$/.test(dateString) ||
    /[+-]\d{4}$/.test(dateString)
  ) {
    const parsed = new Date(dateString)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  // Backend sends UTC LocalDateTime without zone: yyyy-MM-dd'T'HH:mm:ss[.SSS]
  const match = dateString.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/,
  )

  if (!match) {
    const fallback = new Date(dateString)
    return Number.isNaN(fallback.getTime()) ? null : fallback
  }

  const [, year, month, day, hour, minute, second = '0', ms = '0'] = match
  const milliseconds = Number(ms.padEnd(3, '0'))

  return new Date(
    Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
      milliseconds,
    ),
  )
}

const EmailHistoryTable = ({ showNotification }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const {
    emailHistory,
    loading,
    currentPage,
    totalPages,
    totalItems,
    loadEmailHistory,
    handlePageChange,
    retryFailedEmails,
  } = useEmailManagement()
  const [detailsDialog, setDetailsDialog] = useState({
    open: false,
    email: null,
  })

  useEffect(() => {
    loadEmailHistory()
  }, [loadEmailHistory])

  const handleViewDetails = (email) => {
    setDetailsDialog({ open: true, email })
  }

  const handleRetry = async (emailHistoryId) => {
    try {
      await retryFailedEmails(emailHistoryId)
      showNotification('Retry initiated for failed recipients', 'success')
    } catch (error) {
      showNotification(error.message || 'Failed to retry emails', 'error')
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'

    const parsedDate = parseBackendUtcDate(dateString)
    if (!parsedDate) return dateString

    const day = String(parsedDate.getDate()).padStart(2, '0')
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0')
    const year = parsedDate.getFullYear()

    const minutes = String(parsedDate.getMinutes()).padStart(2, '0')
    const seconds = String(parsedDate.getSeconds()).padStart(2, '0')
    const meridiem = parsedDate.getHours() >= 12 ? 'PM' : 'AM'
    const hour12 = parsedDate.getHours() % 12 || 12
    const hours = String(hour12).padStart(2, '0')

    return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds} ${meridiem}`
  }

  const getRecipientTypeLabel = (type) => {
    const labels = {
      TEAM_ONLY: 'Team Only (official email addresses)',
      ALL_USERS: 'All Users',
      CUSTOM: 'Custom',
    }
    return labels[type] || type
  }

  const getRecipientEmails = (email) => {
    if (!email) return []

    const rawRecipientEmails =
      email.recipientEmails || email.recipients || email.customRecipients || []

    const recipientEmails = Array.isArray(rawRecipientEmails)
      ? rawRecipientEmails
      : typeof rawRecipientEmails === 'string'
        ? rawRecipientEmails.split(',')
        : []

    const seen = new Set()
    return recipientEmails
      .map((value) => String(value || '').trim())
      .filter((value) => {
        if (!value) return false
        const normalizedValue = value.toLowerCase()
        if (seen.has(normalizedValue)) return false
        seen.add(normalizedValue)
        return true
      })
  }

  const getRecipientPreview = (email) => {
    const recipientEmails = getRecipientEmails(email)
    if (recipientEmails.length === 0) return ''
    if (recipientEmails.length === 1) return recipientEmails[0]
    return `${recipientEmails[0]} +${recipientEmails.length - 1} more`
  }

  const detailsRecipientEmails = getRecipientEmails(detailsDialog.email)

  return (
    <Box>
      <Typography variant='h5' sx={{ fontWeight: 600, mb: 2 }}>
        Email History
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer
            component={Paper}
            sx={{ boxShadow: 1, borderRadius: 2 }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Recipients</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Success/Failed</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emailHistory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align='center' sx={{ py: 4 }}>
                      <Typography color='text.secondary'>
                        No email history found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  emailHistory.map((email) => (
                    <TableRow key={email.id} hover>
                      <TableCell>{formatDate(email.sentAt)}</TableCell>
                      <TableCell>{email.subject}</TableCell>
                      <TableCell>
                        <Chip
                          label={getEmailTypeLabel(email.emailType)}
                          size='small'
                          variant='outlined'
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant='body2'>
                            {getRecipientTypeLabel(email.recipientType)}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {email.recipientCount} total
                          </Typography>
                          {getRecipientEmails(email).length > 0 && (
                            <Tooltip
                              title={getRecipientEmails(email).join(', ')}
                              placement='top-start'
                            >
                              <Typography
                                variant='caption'
                                color='text.secondary'
                                sx={{
                                  display: 'block',
                                  maxWidth: 260,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  cursor: 'help',
                                }}
                              >
                                {getRecipientPreview(email)}
                              </Typography>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={email.status}
                          size='small'
                          color={STATUS_COLORS[email.status] || 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant='body2' color='success.main'>
                            ✓ {email.successCount || 0} sent
                          </Typography>
                          {email.failedCount > 0 && (
                            <Typography variant='body2' color='error.main'>
                              ✗ {email.failedCount} failed
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title='View Details'>
                          <IconButton
                            size='small'
                            onClick={() => handleViewDetails(email)}
                            title='View Details'
                            sx={{ color: '#232536' }}
                          >
                            <Visibility fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        {email.status === 'PARTIAL' ||
                        email.status === 'FAILED' ? (
                          <IconButton
                            size='small'
                            onClick={() => handleRetry(email.id)}
                            title='Retry Failed'
                            sx={{ color: 'primary.main' }}
                          >
                            <Refresh fontSize='small' />
                          </IconButton>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(event, value) => handlePageChange(value)}
                color='primary'
              />
            </Box>
          )}
        </>
      )}

      {/* Details Dialog */}
      <Dialog
        open={detailsDialog.open}
        onClose={() => setDetailsDialog({ open: false, email: null })}
        maxWidth='xl'
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Email Details</DialogTitle>
        <DialogContent
          dividers
          sx={{
            px: { xs: 2, md: 3 },
            py: { xs: 2, md: 2.5 },
          }}
        >
          {detailsDialog.email && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    pr: { md: 1 },
                    maxHeight: { xs: 'none', md: '68vh' },
                    overflowY: { xs: 'visible', md: 'auto' },
                  }}
                >
                  <MuiTypography variant='subtitle2' sx={{ fontWeight: 700 }}>
                    Subject
                  </MuiTypography>
                  <MuiTypography
                    variant='body2'
                    sx={{ mb: 2, wordBreak: 'break-word' }}
                  >
                    {detailsDialog.email.subject}
                  </MuiTypography>

                  <MuiTypography variant='subtitle2' sx={{ fontWeight: 700 }}>
                    Status
                  </MuiTypography>
                  <Chip
                    label={detailsDialog.email.status}
                    size='small'
                    color={STATUS_COLORS[detailsDialog.email.status] || 'default'}
                    sx={{ mb: 2 }}
                  />

                  <MuiTypography variant='subtitle2' sx={{ fontWeight: 700 }}>
                    Email Type
                  </MuiTypography>
                  <MuiTypography variant='body2' sx={{ mb: 2 }}>
                    {getEmailTypeLabel(detailsDialog.email.emailType)}
                  </MuiTypography>

                  <MuiTypography variant='subtitle2' sx={{ fontWeight: 700 }}>
                    Recipients
                  </MuiTypography>
                  <MuiTypography variant='body2' sx={{ mb: 2 }}>
                    {getRecipientTypeLabel(detailsDialog.email.recipientType)} (
                    {detailsDialog.email.recipientCount} total)
                    <br />
                    Success: {detailsDialog.email.successCount || 0}
                    <br />
                    Failed: {detailsDialog.email.failedCount || 0}
                  </MuiTypography>

                  <MuiTypography variant='subtitle2' sx={{ fontWeight: 700 }}>
                    Recipient Email Addresses
                  </MuiTypography>
                  {detailsRecipientEmails.length > 0 ? (
                    <Box
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 1,
                        p: 1.5,
                        maxHeight: 180,
                        overflow: 'auto',
                        mb: 2,
                      }}
                    >
                      {detailsRecipientEmails.map((recipientEmail) => (
                        <MuiTypography
                          key={recipientEmail}
                          variant='body2'
                          sx={{ mb: 0.75, wordBreak: 'break-word' }}
                        >
                          {recipientEmail}
                        </MuiTypography>
                      ))}
                    </Box>
                  ) : (
                    <MuiTypography
                      variant='body2'
                      color='text.secondary'
                      sx={{ mb: 2 }}
                    >
                      Recipient email addresses are not available for this send.
                    </MuiTypography>
                  )}

                  <MuiTypography variant='subtitle2' sx={{ fontWeight: 700 }}>
                    Sent At
                  </MuiTypography>
                  <MuiTypography variant='body2' sx={{ mb: 2 }}>
                    {formatDate(detailsDialog.email.sentAt)}
                  </MuiTypography>

                  {detailsDialog.email.failedRecipients &&
                    detailsDialog.email.failedRecipients.length > 0 && (
                      <>
                        <MuiTypography
                          variant='subtitle2'
                          sx={{ fontWeight: 700, mt: 2 }}
                        >
                          Failed Recipients
                        </MuiTypography>
                        <Box
                          sx={{
                            border: '1px solid #e5e7eb',
                            borderRadius: 1,
                            p: 1.5,
                            maxHeight: 220,
                            overflow: 'auto',
                            mb: 2,
                          }}
                        >
                          {detailsDialog.email.failedRecipients.map(
                            (failed, index) => (
                              <MuiTypography
                                key={index}
                                variant='body2'
                                sx={{ mb: 1, wordBreak: 'break-word' }}
                              >
                                {failed.email}: {failed.reason}
                              </MuiTypography>
                            ),
                          )}
                        </Box>
                      </>
                    )}
                </Box>
              </Grid>

              <Grid item xs={12} md={8}>
                <Box sx={{ pl: { md: 1 } }}>
                  <MuiTypography variant='subtitle2' sx={{ fontWeight: 700 }}>
                    Template Preview
                  </MuiTypography>
                  <Divider sx={{ my: 1.5 }} />
                  <Box
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      p: { xs: 1, md: 2 },
                      mt: 1,
                      bgcolor: '#fafafa',
                      minHeight: { xs: 320, md: 520 },
                      maxHeight: { xs: 480, md: '68vh' },
                      overflow: 'auto',
                    }}
                    dangerouslySetInnerHTML={{
                      __html:
                        detailsDialog.email.content ||
                        '<p style="margin:0;color:#6b7280;">No content available.</p>',
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDetailsDialog({ open: false, email: null })}
            variant='outlined'
              sx={{
                borderColor: '#232536',
                color: '#232536',          
                '&:hover': {
                  borderColor: '#3a3e5a',
                  color: '#3a3e5a',
                },
              }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default EmailHistoryTable
